import { type HTMLProps } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';

type variant = 'h0' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface HeadingProps
  extends Omit<HTMLProps<HTMLHeadingElement>, 'ref' | 'as'> {
  /** Required: the semantic html header and corresponding styles */
  css?: string;
  id?: string;
  renderAs: variant;
}

export const HeadingZeroStyles = css`
  font-family: ${({ theme }) => theme.fontFamily.DISPLAY};
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: 74px;
  line-height: ${({ theme }) => theme.lineHeight.HEADING};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};

  && {
    @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
      font-size: 60px;
    }

    @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
      font-size: 42px;
    }
  }
`;

export const HeadingOneStyles = css`
  font-family: ${({ theme }) => theme.fontFamily.DISPLAY};
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: 60px;
  line-height: ${({ theme }) => theme.lineHeight.HEADING};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};

  && {
    @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
      font-size: 50px;
    }

    @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
      font-size: 42px;
    }
  }
`;

export const HeadingTwoStyles = css`
  font-family: ${({ theme }) => theme.fontFamily.HEADING};
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: 40px;
  line-height: ${({ theme }) => theme.lineHeight.HEADING};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};

  && {
    @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
      font-size: 30px;
    }
  }
`;

export const HeadingThreeStyles = css`
  font-family: ${({ theme }) => theme.fontFamily.HEADING};
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: 28px;
  line-height: ${({ theme }) => theme.lineHeight.HEADING};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    font-size: 20px;
  }
`;

export const HeadingFourStyles = css`
  font-family: ${({ theme }) => theme.fontFamily.HEADING};
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.SEMI_BOLD};
  font-size: 20px;
  line-height: ${({ theme }) => theme.lineHeight.HEADING};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};
`;

export const HeadingFiveStyles = css`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-weight: ${({ theme }) => theme.fontWeight.SEMI_BOLD};
  font-family: ${({ theme }) => theme.fontFamily.SANS};
  letter-spacing: ${({ theme }) => theme.letterSpacing.HEADING};
  text-transform: uppercase;
`;

export const HeadingSixStyles = css`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  letter-spacing: 3px;
  text-transform: uppercase;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    font-size: ${({ theme }) => theme.fontSize.SMALL};
  }
`;

interface HeadingStyleProps {
  $renderAs: variant;
  css?: string;
}

const HeadingInternal = styled.h1<HeadingStyleProps>`
  margin: 0;
  ${({ $renderAs }) => {
    switch ($renderAs) {
      case 'h0':
        return HeadingZeroStyles;
      case 'h1':
        return HeadingOneStyles;
      case 'h2':
        return HeadingTwoStyles;
      case 'h3':
        return HeadingThreeStyles;
      case 'h4':
        return HeadingFourStyles;
      case 'h5':
        return HeadingFiveStyles;
      case 'h6':
        return HeadingSixStyles;
      default:
        return HeadingOneStyles;
    }
  }}
`;

export const Heading: React.ComponentType<HeadingProps> = React.forwardRef<
  HTMLHeadingElement,
  HeadingProps
>(function Heading({ children, css, renderAs, id, ...htmlProps }, ref) {
  return (
    <HeadingInternal
      ref={ref}
      $renderAs={renderAs}
      as={renderAs === 'h0' ? 'h1' : renderAs}
      css={css}
      id={id}
      {...htmlProps}>
      {children}
    </HeadingInternal>
  );
});
